import { LanguageService } from './../service/language.service';
import { Component, OnInit, AfterViewChecked, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NGXLogger } from 'ngx-logger';
import { environment } from 'src/environments/environment';
import { AuthenticationService } from '../authentication/authentication.service';
import { User } from 'src/app/user/model/user';
import { UserService } from '../service/user.service';
import { MessageHandlerService } from '../utility/message-handler.service';
import { MessengerService, MessageSubject } from '../service/messenger.service';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { NgForm } from '@angular/forms';
import { FaviconService } from '../service/favicon.service';
import { SelectRoleComponent } from 'src/app/shared/select-role/select-role.component';
import { FhirService } from '../service/fhir.service';
import { SettingService } from 'src/app/core/service/setting.service';
import { ChangePasswordComponent } from 'src/app/shared/change-password/change-password.component';
import { DocumentService } from '../service/document.service';
import { SelectOrganizationComponent } from 'src/app/shared/select-organization/select-organization.component';
import { TooltipDirective } from '@progress/kendo-angular-tooltip';

@Component({
  standalone: false,
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})

export class LoginComponent implements OnInit, OnDestroy, AfterViewChecked
{
  @ViewChild(TooltipDirective) public tooltipDir: TooltipDirective;
  @ViewChild('form') public form: NgForm;
  @ViewChild('selectRole') public selectRole: SelectRoleComponent;
  @ViewChild('selectOrganization') public selectOrganization: SelectOrganizationComponent;
  @ViewChild('changePassword') public changePassword: ChangePasswordComponent;

  public selectedLanguage: string;
  public model: any;
  public modelReset: any;
  public isInvalidLogin: boolean;
  public isEhrOnlyAccess: boolean;
  public isInvalidReset: boolean;
  public languages: Array<{ text: string; value: string; primary: boolean }>;
  public loginResult = '';
  public resetPassword = false;
  public mrn: string;
  public providedMRN: string;
  public viewMode: string;
  public authenticating = false;
  public showEHRLinkMessage = false;
  public loginLanguages = [];
  public brandDocumentId = null;
  public brandImageLoaded = false;
  public hasBrandImage = false;
  public brandImageUrl: string;

  private returnUrl = '';

  /**
   * Event handler when authentication state has been changed.
   */
  private authServiceSubscription: Subscription;

  constructor(
    private authService: AuthenticationService,
    private translate: TranslateService,
    private userService: UserService,
    private messageHandlerService: MessageHandlerService,
    private logger: NGXLogger,
    private elem: ElementRef,
    private languageService: LanguageService,
    private messengerService: MessengerService,
    private route: ActivatedRoute,
    private fhirService: FhirService,
    private faviconService: FaviconService,
    private documentService: DocumentService,
    private settingService: SettingService)
  {
    this.model = { username: '', password: '', version: environment.channelsVersion };
    this.modelReset = { username: '' };
    this.isInvalidLogin = false;
    this.isEhrOnlyAccess = false;
    this.isInvalidReset = false;
    this.languages = this.languageService.languages;
    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        const loginLanguagesSetting = sessionSetting.loginLanguages ? sessionSetting.loginLanguages.split(',') : [];
        this.loginLanguages = this.languageService.languages.filter(x => loginLanguagesSetting.includes(x.value));
        this.brandDocumentId = sessionSetting.brandLogoDocumentId;
        this.getBrandPhotoFromService();
      }
    });

    authService.reset();

    this.setLanguageDefault();
  }

  public ngOnInit(): void
  {
    this.mrn = this.route.snapshot.queryParams.mrn || null;
    this.providedMRN = this.route.snapshot.queryParams.providedMRN || null;
    this.viewMode = this.route.snapshot.queryParams.viewMode || null;
    this.authServiceSubscription = this.authService.authenticationChange.subscribe({
      next: () =>
      {
        this.authenticating = false;
      }
    });

    this.route.queryParams.subscribe({
      next: (params) =>
      {
        this.returnUrl = params.return || '';
      }
    });

    this.faviconService.setAppIcon();

    // FHIR issuer
    this.showEHRLinkMessage = this.fhirService.issuer;
  }

  public ngOnDestroy(): void
  {
    if (this.authServiceSubscription !== undefined) { this.authServiceSubscription.unsubscribe(); }
  }

  public ngAfterViewChecked(): void
  {
    this.initView();
  }

  public login(): void
  {
    this.authenticating = true;
    this.isInvalidLogin = false;
    this.isEhrOnlyAccess = false;
    this.authService.authenticate(this.model.username, this.model.password, this.selectedLanguage).subscribe({
      next: (data) =>
      {
        const authenticatedUser = data as User;
        if (!authenticatedUser.enabled || authenticatedUser.servicesAccount || (authenticatedUser.linkedFHIRUser && authenticatedUser.isEHROnlyAccess) ||
          authenticatedUser.role === 'ROLE_COMMUNITY_USER')
        {
          // Use the generic invalid username or password for disabled account
          this.translate.get(['INVALID-USERNAME-OR-PASSWORD', 'INVALID-LOGIN-EHR-ONLY-ACCESS']).subscribe({
            next: (value) =>
            {
              this.isEhrOnlyAccess = authenticatedUser.linkedFHIRUser && authenticatedUser.isEHROnlyAccess;
              this.loginResult = this.isEhrOnlyAccess ? value['INVALID-LOGIN-EHR-ONLY-ACCESS'] : value['INVALID-USERNAME-OR-PASSWORD'];
              this.isInvalidLogin = true;
              this.authenticating = false;
            }
          });
        }
        else
        {
          // authenticate into Flowable IDM
          this.authService.authenticateFlowable(this.model.username, this.model.password).subscribe({
            error: () =>
            {
              this.authenticating = false;
              this.translate.get('MODELER-AUTHENTICATE-ERROR').subscribe({
                next: (value) =>
                {
                  this.logger.error(value);
                }
              });
            }
          });

          // Set url params if any
          this.authService.login(authenticatedUser, this.returnUrl,
            this.selectRole, this.selectOrganization, this.changePassword);
          this.isInvalidLogin = false;
        }
      },
      error: (error) =>
      {
        if (error.status === 401)
        {
          this.translate.get('INVALID-USERNAME-OR-PASSWORD').subscribe({
            next: (value) =>
            {
              this.loginResult = value;
            }
          });
        }
        else if (error.status === 423)
        {
          this.translate.get('ACCOUNT-DISABLED').subscribe({
            next: (value) =>
            {
              this.loginResult = value;
            }
          });
        }
        else if (error.status === 500 || error.status === 0)
        {
          this.translate.get('LOST-SERVER-CONNECTION').subscribe({
            next: (value) =>
            {
              this.loginResult = value;
            }
          });
        }
        else
        {
          this.translate.get('INVALID-USERNAME-OR-PASSWORD').subscribe({
            next: (value) =>
            {
              this.loginResult = value;
            }
          });
        }
        this.isInvalidLogin = true;
        this.authenticating = false;
      }
    });
  }

  public switchLanguage(language: string): void
  {
    this.selectedLanguage = language;
    this.languageService.switchLanguage(language);
  }

  public changeLanguage(newLanguage: string): void
  {
    if (newLanguage !== this.selectedLanguage)
    {
      this.switchLanguage(newLanguage);
      this.languageService.setLanguage(newLanguage);

      // Notify any subscriber that language have been changed.
      const languageChanged: any = {};
      languageChanged.newLanguage = newLanguage;
      const languageSubject = new MessageSubject('languageChanged', languageChanged);
      this.messengerService.sendMessage(languageSubject);
    }
  }

  public resetErrors(): void 
  {
    this.isEhrOnlyAccess = false;
    this.isInvalidLogin = false;
  }

  public sendRequest(): void
  {
    this.userService.sendForgotPasswordEmail(this.modelReset.email).subscribe({
      next: () =>
      {
        this.messageHandlerService.displaySuccess('PASSWORD-RESET-REQUESTED');
        this.resetPassword = false;
        this.modelReset = { username: '' };
      },
      error: () =>
      {
        this.resetPassword = false;
      }
    });
  }

  public loginKeyDown(event: KeyboardEvent): void
  {
    if (event && event.key === 'Enter' && !this.authenticating && this.form.valid)
    {
      event.preventDefault();
      this.login();
    }
  }

  public getChannelsEdition(): string
  {
    return 'Channels';
  }

  public showTooltip(e: ElementRef): void
  {
    this.tooltipDir.toggle(e, true);
  }
  
  public hideTooltip(): void
  {
    this.tooltipDir.hide();
  }

  private initView(): void
  {
    const elements = this.elem.nativeElement.querySelectorAll('.k-label');
    for (let i = 0; i < elements.length; i++)
    {
      elements[i].setAttribute('style', 'padding-left: 20px;');
    }
  }

  private setLanguageDefault(): void
  {
    this.selectedLanguage = this.languageService.currentLanguage;
    this.switchLanguage(this.selectedLanguage);
  }


  private getBrandPhotoFromService()
  {
    this.brandImageLoaded = false;
    this.hasBrandImage = false;
    if (this.brandDocumentId !== null && this.brandDocumentId > 0)
    {
      this.documentService.retrieveBrandImageDocument().subscribe({
        next: (data) =>
        {
          this.hasBrandImage = data.content !== null;
          if (this.hasBrandImage)
          {
            const base64 = data.content;
            this.brandImageUrl = 'data:image/jpeg;base64,' + base64;
          }
          this.brandImageLoaded = true;
        }
      });
    }
  }
}
